import React from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, CircularProgress, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import Layout from './components/Layout';
import LoginScreen from './components/LoginScreen';
import Dashboard from './pages/Dashboard';
import CallHistory from './pages/CallHistory';
import BlockedNumbers from './pages/BlockedNumbers';
import Configuration from './pages/Configuration';
import Statistics from './pages/Statistics';
import SipSimulator from './pages/SipSimulator';
import SipTraceViewer from './pages/SipTraceViewer';
import CostSummary from './pages/CostSummary';
import RiskMeter from './pages/RiskMeter';
import { api, AUTH_TOKEN_EXPIRED_EVENT } from './services/api';

const THEME_PROFILE_STORAGE_KEY = 'scammerDetectionThemeProfile';
const DEFAULT_THEME_PROFILE = 'enterprise-admin';
const THEME_PROFILE_OPTIONS = ['enterprise-admin', 'classic-dark', 'high-contrast'];

const normalizeThemeProfile = (value) => (
  THEME_PROFILE_OPTIONS.includes(String(value || '').trim().toLowerCase())
    ? String(value).trim().toLowerCase()
    : DEFAULT_THEME_PROFILE
);

const getThemeProfileConfig = (profile) => {
  switch (normalizeThemeProfile(profile)) {
    case 'classic-dark':
      return {
        mode: 'dark',
        primary: '#60A5FA',
        primaryDark: '#2563EB',
        secondary: '#818CF8',
        background: '#0F172A',
        paper: '#111827',
        drawer: '#0B1120',
        drawerSelected: 'rgba(96,165,250,0.18)',
        tableHead: '#1E293B',
        textPrimary: '#F8FAFC',
        textSecondary: '#CBD5E1',
        divider: 'rgba(148,163,184,0.18)',
        cardShadow: '0 10px 30px rgba(0,0,0,0.28)',
      };
    case 'high-contrast':
      return {
        mode: 'dark',
        primary: '#38BDF8',
        primaryDark: '#0284C7',
        secondary: '#FACC15',
        background: '#000000',
        paper: '#0A0A0A',
        drawer: '#000000',
        drawerSelected: 'rgba(56,189,248,0.28)',
        tableHead: '#111111',
        textPrimary: '#FFFFFF',
        textSecondary: '#E5E7EB',
        divider: 'rgba(255,255,255,0.28)',
        cardShadow: 'none',
      };
    case 'enterprise-admin':
    default:
      return {
        mode: 'light',
        primary: '#2563EB',
        primaryDark: '#1D4ED8',
        secondary: '#4F46E5',
        background: '#F3F6FA',
        paper: '#FFFFFF',
        drawer: '#111827',
        drawerSelected: 'rgba(37,99,235,0.22)',
        tableHead: '#E8EEF6',
        textPrimary: '#111827',
        textSecondary: '#64748B',
        divider: 'rgba(15,23,42,0.12)',
        cardShadow: '0 10px 28px rgba(15,23,42,0.08)',
      };
  }
};

const buildTheme = (profile) => {
  const profileConfig = getThemeProfileConfig(profile);
  const isDark = profileConfig.mode === 'dark';

  return createTheme({
    palette: {
      mode: profileConfig.mode,
      primary: {
        main: profileConfig.primary,
        light: isDark ? '#93C5FD' : '#60A5FA',
        dark: profileConfig.primaryDark,
        contrastText: '#ffffff',
      },
      secondary: {
        main: profileConfig.secondary,
        light: isDark ? '#A5B4FC' : '#818CF8',
        dark: isDark ? '#4F46E5' : '#3730A3',
        contrastText: '#ffffff',
      },
      success: {
        main: isDark ? '#4ADE80' : '#16A34A',
      },
      warning: {
        main: isDark ? '#FBBF24' : '#D97706',
      },
      error: {
        main: isDark ? '#F87171' : '#DC2626',
      },
      background: {
        default: profileConfig.background,
        paper: profileConfig.paper,
      },
      text: {
        primary: profileConfig.textPrimary,
        secondary: profileConfig.textSecondary,
      },
      divider: profileConfig.divider,
    },
    shape: {
      borderRadius: 6,
    },
    typography: {
      fontFamily: [
        'Inter',
        'Segoe UI',
        'Roboto',
        'Helvetica',
        'Arial',
        'sans-serif',
      ].join(','),
      h4: {
        fontWeight: 700,
      },
      h5: {
        fontWeight: 700,
      },
      h6: {
        fontWeight: 700,
      },
      button: {
        fontWeight: 700,
        letterSpacing: 0,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: profileConfig.background,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: profileConfig.paper,
            color: profileConfig.textPrimary,
            borderBottom: `1px solid ${profileConfig.divider}`,
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: profileConfig.drawer,
            color: '#F8FAFC',
            borderRight: `1px solid ${profileConfig.divider}`,
            '& .MuiTypography-root': {
              color: '#F8FAFC',
            },
            '& .MuiIconButton-root': {
              color: '#CBD5E1',
            },
            '& .MuiListItemIcon-root': {
              color: '#94A3B8',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderColor: profileConfig.divider,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${profileConfig.divider}`,
            boxShadow: profileConfig.cardShadow,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: profileConfig.divider,
          },
          head: {
            color: profileConfig.textPrimary,
            fontWeight: 700,
            backgroundColor: profileConfig.tableHead,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '2px 8px',
            color: '#CBD5E1',
            '&.Mui-selected': {
              backgroundColor: profileConfig.drawerSelected,
              color: '#FFFFFF',
              '& .MuiListItemIcon-root': {
                color: '#93C5FD',
              },
            },
            '&.Mui-selected:hover': {
              backgroundColor: isDark ? 'rgba(96,165,250,0.24)' : 'rgba(37,99,235,0.24)',
            },
            '&:hover': {
              backgroundColor: isDark ? 'rgba(148,163,184,0.10)' : 'rgba(226,232,240,0.12)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 700,
          },
        },
      },
    },
  });
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const DEFAULT_INACTIVITY_TIMEOUT_MINUTES = 30;
const LAST_ACTIVITY_STORAGE_KEY = 'scammerDetectionLastActivityAt';
const AUTH_CONFIG_UPDATED_EVENT = 'scammerDetectionAuthConfigUpdated';
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'pointerdown'];

const normalizeInactivityTimeoutMinutes = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return DEFAULT_INACTIVITY_TIMEOUT_MINUTES;
  }
  return Math.min(parsed, 1440);
};

function App() {
  const [themeProfile, setThemeProfile] = React.useState(() => (
    normalizeThemeProfile(window.localStorage.getItem(THEME_PROFILE_STORAGE_KEY))
  ));
  const [initializing, setInitializing] = React.useState(true);
  const [loginLoading, setLoginLoading] = React.useState(false);
  const [authEnabled, setAuthEnabled] = React.useState(false);
  const [authenticated, setAuthenticated] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState(null);
  const [logoutMessage, setLogoutMessage] = React.useState('');
  const [inactivityTimeoutMinutes, setInactivityTimeoutMinutes] = React.useState(DEFAULT_INACTIVITY_TIMEOUT_MINUTES);
  const themeMode = getThemeProfileConfig(themeProfile).mode;
  const theme = React.useMemo(() => buildTheme(themeProfile), [themeProfile]);

  const toggleThemeMode = React.useCallback(() => {
    setThemeProfile((currentProfile) => {
      const nextProfile = getThemeProfileConfig(currentProfile).mode === 'dark'
        ? DEFAULT_THEME_PROFILE
        : 'classic-dark';
      window.localStorage.setItem(THEME_PROFILE_STORAGE_KEY, nextProfile);
      return nextProfile;
    });
  }, []);

  const loadAdminConfig = React.useCallback(async () => {
    try {
      const config = await api.getConfiguration();
      setInactivityTimeoutMinutes(
        normalizeInactivityTimeoutMinutes(config?.sessionInactivityTimeoutMinutes)
      );
      const nextThemeProfile = normalizeThemeProfile(config?.themeProfile);
      window.localStorage.setItem(THEME_PROFILE_STORAGE_KEY, nextThemeProfile);
      setThemeProfile(nextThemeProfile);
    } catch (error) {
      setInactivityTimeoutMinutes(DEFAULT_INACTIVITY_TIMEOUT_MINUTES);
    }
  }, []);

  const logoutForInactivity = React.useCallback(() => {
    api.clearAuthToken();
    window.localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    queryClient.clear();
    setCurrentUser(null);
    setAuthenticated(false);
    setLogoutMessage('You were signed out after a period of inactivity.');
  }, []);

  const handleLogout = React.useCallback(() => {
    api.clearAuthToken();
    window.localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    queryClient.clear();
    setCurrentUser(null);
    setAuthenticated(false);
    setLogoutMessage('You have signed out.');
  }, []);

  const handleTokenExpired = React.useCallback(() => {
    window.localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    queryClient.clear();
    setCurrentUser(null);
    setAuthenticated(false);
    setLogoutMessage('Your session expired. Please sign in again.');
  }, []);

  React.useEffect(() => {
    const bootstrapAuth = async () => {
      try {
        const status = await api.getAuthStatus();
        const enabled = Boolean(status?.authEnabled);
        setAuthEnabled(enabled);

        if (!enabled) {
          await loadAdminConfig();
          setCurrentUser({
            username: 'local-poc',
            roles: ['admin'],
          });
          setAuthenticated(true);
          return;
        }

        const token = api.getAuthToken();

        if (!token) {
          setAuthenticated(false);
          return;
        }

        const currentUserResponse = await api.getCurrentUser();
        await loadAdminConfig();
        setCurrentUser(currentUserResponse?.user || null);
        setAuthenticated(true);
      } catch (error) {
        api.clearAuthToken();
        setCurrentUser(null);
        setAuthenticated(false);
      } finally {
        setInitializing(false);
      }
    };

    bootstrapAuth();
  }, [loadAdminConfig]);

  React.useEffect(() => {
    if (!authEnabled || !authenticated) {
      return undefined;
    }

    const handleConfigUpdated = () => {
      loadAdminConfig();
    };

    window.addEventListener(AUTH_CONFIG_UPDATED_EVENT, handleConfigUpdated);
    return () => window.removeEventListener(AUTH_CONFIG_UPDATED_EVENT, handleConfigUpdated);
  }, [authEnabled, authenticated, loadAdminConfig]);

  React.useEffect(() => {
    window.addEventListener(AUTH_TOKEN_EXPIRED_EVENT, handleTokenExpired);
    return () => window.removeEventListener(AUTH_TOKEN_EXPIRED_EVENT, handleTokenExpired);
  }, [handleTokenExpired]);

  React.useEffect(() => {
    if (!authEnabled || !authenticated || inactivityTimeoutMinutes <= 0) {
      return undefined;
    }

    const timeoutMs = inactivityTimeoutMinutes * 60 * 1000;
    const markActivity = () => {
      window.localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
    };
    const readLastActivity = () => {
      const stored = Number.parseInt(window.localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || '', 10);
      return Number.isFinite(stored) && stored > 0 ? stored : Date.now();
    };
    const checkIdle = () => {
      if (Date.now() - readLastActivity() >= timeoutMs) {
        logoutForInactivity();
      }
    };

    markActivity();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });
    const interval = window.setInterval(checkIdle, 10000);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity);
      });
      window.clearInterval(interval);
    };
  }, [authEnabled, authenticated, inactivityTimeoutMinutes, logoutForInactivity]);

  const handleLogin = async ({ username, password }) => {
    setLoginLoading(true);

    try {
      const loginResponse = await api.login({ username, password });
      const currentUserResponse = loginResponse?.user
        ? loginResponse
        : await api.getCurrentUser();
      await loadAdminConfig();
      window.localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
      setCurrentUser(currentUserResponse?.user || null);
      setLogoutMessage('');
      setAuthenticated(true);
    } finally {
      setLoginLoading(false);
    }
  };

  if (initializing) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (authEnabled && !authenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginScreen
          onLogin={handleLogin}
          loading={loginLoading}
          message={logoutMessage}
          themeMode={themeMode}
          onToggleThemeMode={toggleThemeMode}
        />
      </ThemeProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/sip-trace" element={<SipTraceViewer />} />
            <Route
              path="*"
              element={(
                <Layout
                  themeMode={themeMode}
                  onToggleThemeMode={toggleThemeMode}
                  currentUser={currentUser}
                  onLogout={authEnabled ? handleLogout : null}
                >
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/history" element={<CallHistory />} />
                    <Route path="/blocked" element={<BlockedNumbers />} />
                    <Route path="/config" element={<Configuration />} />
                    <Route path="/sip-sim" element={<SipSimulator />} />
                    <Route path="/stats" element={<Statistics />} />
                    <Route path="/risk-meter" element={<RiskMeter />} />
                    <Route path="/cost-summary" element={<CostSummary />} />
                    <Route path="/ai2-costs" element={<Navigate to="/cost-summary" replace />} />
                  </Routes>
                </Layout>
              )}
            />
          </Routes>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
